import { Input, Dropdown, Button, Radio } from '@/components/atoms/index'
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import TrophyBadgeCard from '@/components/organisms/Card/TrohyBadgeCard';
import AcademyStudentTable from '@/components/organisms/Table/AcademyStudentTable';
import StudentSelectionContent from '@/components/Modal/StudentSelectionContent';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import { academyTrophyBadgeApi, academyStudentApi } from '@/services/api';
import { message, Modal } from 'antd';
import { useSessionSlug } from '@/hooks/useSessionSlug';

interface TrophyBadgeMaster {
  id: number;
  name: string;
  description?: string;
  iconUrl?: string;
  type: 'trophy' | 'badge';
  grade?: number;
}

interface StudentData {
  id: string;
  name: string;
  email: string;
  grade: string;
  status: 'active' | 'inactive';
  registrationDate: string;
  academyCode: string;
  birthDate: string;
  loginId?: string;
}

export default function AcademyStudentTrophyBadgeDetail() {
    const navigate = useNavigate();
    const { pathname, state } = useLocation();
    const slugPrefix = getSlugPrefix(pathname);
    const { awardId } = useParams<{ awardId: string }>();
    const isEditMode = awardId && awardId !== 'new';
    const fromPath = (state as { from?: string } | null)?.from;
    
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // 학원 코드
    const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug();
    const academyCode = sessionAcademyCode || '';
    
    // 폼 데이터
    const [selectedType, setSelectedType] = useState<'trophy' | 'badge' | ''>('');
    const [trophyBadges, setTrophyBadges] = useState<TrophyBadgeMaster[]>([]);
    const [selectedTrophyBadgeId, setSelectedTrophyBadgeId] = useState<number | null>(null);
    const [reason, setReason] = useState('');
    const [awardType, setAwardType] = useState<string>('');
    
    // 수정 모드용 추가 데이터
    const [grantedByName, setGrantedByName] = useState('');
    const [awardedAt, setAwardedAt] = useState('');
    
    // 학생 관련
    const [availableStudents, setAvailableStudents] = useState<StudentData[]>([]);
    const [selectedStudents, setSelectedStudents] = useState<StudentData[]>([]);
    const [isStudentSelectionModalOpen, setIsStudentSelectionModalOpen] = useState(false);
    const [displayPage, setDisplayPage] = useState(1);
    const [displayPageSize] = useState(10);

    // 유형 옵션
    const typeOptions = [
        { label: '유형 선택', value: '' },
        { label: '트로피', value: 'trophy' },
        { label: '뱃지', value: 'badge' },
    ];

    const fetchAwardDetail = useCallback(async () => {
        if (!awardId || !academyCode) return;
        
        setLoading(true);
        try {
            const response = await academyTrophyBadgeApi.getAwardDetail(Number(awardId), academyCode);
            const data = response?.data || response;
            
            console.log('수여 정보 상세 데이터:', data);
            
            // 트로피/뱃지 정보
            const trophyBadge = data?.trophyBadge;
            if (trophyBadge) {
                const badgeType = trophyBadge.type;
                console.log('트로피/뱃지 유형:', badgeType);
                
                // 먼저 유형을 설정
                setSelectedType(badgeType);
                // 선택값을 상세의 배지로 설정(목록은 selectedType 변경에 따른 효과로 로드됨)
                    setSelectedTrophyBadgeId(trophyBadge.id);
            }
            
            // 수여 대상자 정보
            const student = data?.student;
            console.log('수여 대상자 정보:', student);
            if (student) {
                const studentData: StudentData = {
                    id: String(student.id),
                    name: student.name || '-',
                    email: student.email || '-',
                    grade: '-',
                    status: 'active' as const,
                    registrationDate: '-',
                    academyCode: academyCode,
                    birthDate: '-',
                    loginId: student.loginId || student.id // [QA Fix] 기존 수여 데이터에서도 ID 표기
                };
                console.log('변환된 학생 데이터:', studentData);
                setSelectedStudents([studentData]);
            }
            
            // 기타 정보
            const reasonValue = data?.reason || '';
            console.log('수여 사유:', reasonValue);
            setReason(reasonValue);
            setAwardType(data?.awardType || '');
            setGrantedByName(data?.grantedBy?.name || '-');
            setAwardedAt(data?.awardedAt || '');
            
        } catch (error) {
            console.error('수여 정보 조회 실패:', error);
            message.error('수여 정보를 불러오는데 실패했습니다.');
            navigate(`${slugPrefix}/academy/student/trophy_badge/list`);
        } finally {
            setLoading(false);
        }
    }, [awardId, academyCode, navigate]);

    // 수정 모드일 때 기존 데이터 불러오기
    useEffect(() => {
        if (isEditMode && academyCode) {
            fetchAwardDetail();
        }
    }, [isEditMode, academyCode, fetchAwardDetail]);

    // 전체 학생 목록 불러오기 (사용하지 않음 - 모달 내부에서 API 호출)
    // const fetchStudents = useCallback(async () => { ... }, [academyCode]);

    const fetchTrophyBadges = async (type: 'trophy' | 'badge') => {
        try {
            const response = await academyTrophyBadgeApi.listMaster({ type });
            const data = response?.data || response;
            const content = data?.content || [];
            setTrophyBadges(content);
            // 신규 등록 등 선택값이 없을 때만 기본값 자동 선택. 수정 모드에서는 기존 선택 유지
            if (!isEditMode && selectedTrophyBadgeId == null && content.length > 0) {
                setSelectedTrophyBadgeId(content[0].id);
            }
        } catch (error) {
            console.error('트로피/뱃지 마스터 데이터 조회 실패:', error);
            message.error('트로피/뱃지 목록을 불러오는데 실패했습니다.');
            setTrophyBadges([]);
        }
    };

    // 유형 선택 시 트로피/뱃지 마스터 데이터 불러오기 (신규/수정 공통)
    useEffect(() => {
        if (selectedType) {
            fetchTrophyBadges(selectedType);
        } else {
            setTrophyBadges([]);
            setSelectedTrophyBadgeId(null);
        }
    }, [selectedType]);

    // 전체 학생 목록 불러오기 (제거됨)
    // useEffect(() => {
    //    if (academyCode) {
    //        fetchStudents();
    //    }
    // }, [academyCode, fetchStudents]);

    const handleStudentSelect = (student: StudentData) => {
        const isSelected = selectedStudents.find(s => s.id === student.id);
        if (isSelected) {
            setSelectedStudents(prev => prev.filter(s => s.id !== student.id));
        } else {
            setSelectedStudents(prev => [...prev, student]);
        }
    };

    const handleSelectAll = (students: StudentData[]) => {
        setSelectedStudents(students);
    };

    const handleLoadStudents = () => {
        setIsStudentSelectionModalOpen(true);
    };

    const handleDeleteStudent = (student: any) => {
        Modal.confirm({
            title: '학생 삭제',
            content: '해당 학생을 삭제하시겠습니까?',
            okText: '삭제',
            cancelText: '취소',
            onOk: () => {
                setSelectedStudents(prev => prev.filter(s => s.id !== student.id));
                message.success('학생이 삭제되었습니다.');
            }
        });
    };

    const handleSave = async () => {
        // 유효성 검사
        if (!selectedType) {
            message.error('유형을 선택해주세요.');
            return;
        }
        if (!selectedTrophyBadgeId) {
            message.error('트로피/뱃지 디자인을 선택해주세요.');
            return;
        }
        if (selectedStudents.length === 0) {
            message.error('수여 대상자를 선택해주세요.');
            return;
        }

        setSaving(true);
        try {
            if (isEditMode) {
                // 편집 모드: updateAward 호출
                if (selectedStudents.length !== 1) {
                    message.error('수정 시에는 수여 대상자 1명만 선택할 수 있습니다.');
                    setSaving(false);
                    return;
                }
                const updatePayload: any = {
                    trophyBadgeId: selectedTrophyBadgeId!,
                    studentUserId: Number(selectedStudents[0].id),
                };
                if (reason) updatePayload.reason = reason;
                await academyTrophyBadgeApi.updateAward(Number(awardId), updatePayload);
                message.success('수정되었습니다.');
                navigate(`${slugPrefix}/academy/student/trophy_badge/list`);
            } else {
                // 신규 등록 모드
                const payload: any = {
                    academyCode,
                    trophyBadgeId: selectedTrophyBadgeId,
                    studentUserIds: selectedStudents.map(s => Number(s.id)),
                };
                
                if (reason) {
                    payload.reason = reason;
                }

                const response = await academyTrophyBadgeApi.createAward(payload);
                const data = response?.data || response;
                const results = data?.results || [];

                // 성공/실패 결과 확인
                const successCount = results.filter((r: any) => r.success).length;
                const failCount = results.length - successCount;

                if (failCount > 0) {
                    message.warning(`${successCount}명 수여 완료, ${failCount}명 실패`);
                } else {
                    message.success('트로피/뱃지가 성공적으로 수여되었습니다.');
                }

                // 목록 페이지로 이동
                navigate(fromPath || `${slugPrefix}/academy/student/trophy_badge/list`);
            }
        } catch (error) {
            console.error('트로피/뱃지 수여 실패:', error);
            message.error('트로피/뱃지 수여에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(fromPath || `${slugPrefix}/academy/student/trophy_badge/list`);
    };

    const selectedTrophyBadge = trophyBadges.find(tb => tb.id === selectedTrophyBadgeId);

    if (loading) {
        return (
            <main className='flex flex-col flex-1'>
                <div className='flex items-center justify-center h-96'>
                    <p>로딩 중...</p>
                </div>
            </main>
        );
    }

    return (
        <main className='flex flex-col flex-1'>
            <div className='flex flex-row justify-between items-center pt-8'>
                <h1 className='heading1'>{isEditMode ? '트로피 및 뱃지 수여 정보' : '트로피 및 뱃지 수여'}</h1>
            </div>
            <div className="mt-5">

            <ul>
                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>유형</p>
                    <Dropdown 
                        placeholder='유형을 선택해주세요' 
                        className='max-w-[500px] w-full'
                        options={typeOptions}
                        value={selectedType}
                        onChange={(value) => setSelectedType(value as 'trophy' | 'badge' | '')}
                    />
                </li>

                {selectedType && trophyBadges.length > 0 && (
                    <li className='flex py-5 border-b border-gray-200'>
                        <p className='flex-none max-w-[150px] w-full text-gray-500'>트로피 및 뱃지 디자인</p>
                        <section className='grid grid-cols-5 gap-5'>
                            {trophyBadges.map((badge) => (
                                <div key={badge.id} className='flex gap-2 items-start'>
                                    <Radio 
                                        name='trophy-design' 
                                        checked={selectedTrophyBadgeId === badge.id} 
                                        onChange={() => setSelectedTrophyBadgeId(badge.id)}
                                    />
                                    <TrophyBadgeCard 
                                        showLabel={true}
                                        iconUrl={badge.iconUrl}
                                        name={badge.name}
                                        date={badge.description || ''}
                                    />
                                </div>
                            ))}
                        </section>
                    </li>
                )}

                {selectedTrophyBadge && (
                    <li className='flex py-5 border-b border-gray-200'>
                        <p className='flex-none max-w-[150px] w-full text-gray-500'>트로피 및 뱃지 이름</p>
                        <p className='font-semibold'>{selectedTrophyBadge.name}</p>
                    </li>
                )}

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>수여 대상자</p>
                    <section className='w-full'>
                        <div className='flex gap-2 w-full'>
                            <Input 
                                placeholder='수여 대상자를 선택해주세요' 
                                className='max-w-[500px] w-full'
                                value={selectedStudents.length > 0 ? `${selectedStudents.length}명 선택됨` : ''}
                                readOnly
                            />
                            <Button className='flex-none' onClick={handleLoadStudents}>불러오기</Button>
                        </div>
                        {selectedStudents.length > 0 && (
                            <div className='mt-5'>
                                <AcademyStudentTable 
                                    showSearch={false} 
                                    showPagination={true}
                                    data={selectedStudents} 
                                    selectedStudents={selectedStudents} 
                                    onStudentSelect={handleStudentSelect} 
                                    onSelectAll={handleSelectAll} 
                                    type='display' 
                                    displayType='trophy'
                                    currentPage={displayPage}
                                    pageSize={displayPageSize}
                                    total={selectedStudents.length}
                                    onPageChange={(page) => setDisplayPage(page)}
                                    onDelete={handleDeleteStudent}
                                />
                            </div>
                        )}
                    </section>
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>수여 사유</p>
                    <Input 
                        placeholder='수여 사유를 입력해주세요 (선택사항)' 
                        className='max-w-[500px] w-full'
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>수여자</p>
                    <p>{isEditMode ? (awardType === 'auto' ? '자동' : (grantedByName || '-')) : (localStorage.getItem('academy_user_name') || '-')}</p>
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>수여일</p>
                    <p>{isEditMode ? awardedAt : new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '-').replace('.', '')}</p>
                </li>
            </ul>
            <div className='flex justify-end *:w-[120px] gap-2 my-4'>
                <Button intent='secondary' onClick={handleCancel} disabled={saving}>취소</Button>
                <Button onClick={handleSave} disabled={saving}>
                    {saving ? '저장 중...' : (isEditMode ? '수정' : '저장')}
                </Button>
            </div>
            </div>
         <Dialog
             open={isStudentSelectionModalOpen}
             onCancel={() => setIsStudentSelectionModalOpen(false)}
             className="!max-w-[800px] !w-full"
             hideFooter={true}
          >
             <StudentSelectionContent
                 selectionMode={isEditMode ? 'single' : 'multiple'}
                 onOk={(students) => {
                     // 편집 모드에서는 단일만 허용
                     setSelectedStudents(isEditMode ? (students.slice(0, 1)) : students);
                     setIsStudentSelectionModalOpen(false);
                 }}
                 onCancel={() => setIsStudentSelectionModalOpen(false)}
                 title="학생 선택"
                 showAcademyCode={false}
                 showClassName={true}
             />
          </Dialog>
        </main>
    )
}
